import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { LockIcon, PlusIcon, Share2Icon } from 'lucide-react';
import { ShareSheet } from '../../components/engagement/ShareSheet';
import { PageHeader } from '../../components/PageHeader';
import { Button } from '../../components/ui/Button';
import { Card, SectionTitle } from '../../components/ui/Card';
import { Modal } from '../../components/ui/Modal';
import { progressPhotos } from '../../data/engagement';
import { useLocale } from '../../contexts/LocaleContext';

/** MOB/Profile/ProgressPhotos — private-by-default evolution timeline. */
export function ProgressPhotos() {
  const {
    t,
    tl,
    locale
  } = useLocale();
  const navigate = useNavigate();
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [sharing, setSharing] = useState(false);
  const [addNote, setAddNote] = useState(false);
  const dateLocale = locale === 'pt-BR' ? ptBR : undefined;
  const fmt = (iso: string) => format(parseISO(iso), 'dd MMM yyyy', {
    locale: dateLocale
  });
  const latest = progressPhotos[0];
  const first = progressPhotos[progressPhotos.length - 1];
  return <div className="px-2 pb-2" data-id="element-2925" data-mp="c4qh0ab10nvg">
      <PageHeader title={t('eng.photos.title')} subtitle={t('eng.photos.subtitle')} showBack action={<span className="bee-overline flex items-center gap-1 rounded-pill border border-sand-line px-2 py-[5px] text-[8.5px] font-bold text-deep" data-id="element-2927" data-mp="14smbr9h1c5pl">
            <LockIcon size={10} aria-hidden="true" data-id="element-2928" data-mp="18temk75k2inz" />
            {t('eng.photos.private')}
          </span>} data-id="element-2926" data-mp="c5ic7k0pojl7" />

      <Card className="p-2" data-id="element-2929" data-mp="uw9lymom2c04">
        <p className="text-[12px] leading-snug text-muted" data-id="element-2930" data-mp="zt0197sld3kh">{t('eng.photos.privateNote')}</p>
      </Card>

      <section className="mt-3" data-id="element-2931" data-mp="3fjuts8w4ciob">
        <SectionTitle data-id="element-2932" data-mp="3fit6n61tw5q8">{t('eng.photos.compare')}</SectionTitle>
        <div className="grid grid-cols-2 gap-2" data-id="element-2933" data-mp="3kk0f77bsp8xy">
          {[{
          photo: first,
          label: t('eng.photos.first')
        }, {
          photo: latest,
          label: t('eng.photos.latest')
        }].map(({
          photo,
          label
        }) => <figure key={photo.id} className="overflow-hidden rounded-card bg-sand-deep" data-id="element-2934" data-mp="2wh2gatjcv3ik">
              <img src={photo.url} alt={t('eng.photos.alt', {
            date: fmt(photo.date)
          })} className="aspect-[3/4] w-full object-cover" data-id="element-2935" data-mp="2ro0yodrrkpva" />
              <figcaption className="px-2 py-1" data-id="element-2936" data-mp="2hrxxba4qkv9e">
                <span className="bee-overline block text-[8.5px] font-bold text-muted" data-id="element-2937" data-mp="2hoih23zbtg6e">{label}</span>
                <span className="bee-num text-[12px] font-medium text-deep" data-id="element-2938" data-mp="2cu4rii7ci39n">{fmt(photo.date)}</span>
              </figcaption>
            </figure>)}
        </div>
        <Button variant="outline" fullWidth className="mt-2" onClick={() => navigate('/perfil/fotos/antes-depois')} data-id="element-2939" data-mp="23soiads9o770">
          {t('ba.cta')}
        </Button>
      </section>

      <section className="mt-3" data-id="element-2940" data-mp="20s7x7hu53dt">
        <SectionTitle action={<Button variant="quiet" onClick={() => setAddNote(true)} data-id="element-2942" data-mp="9jrq59tk8ovl">
              <PlusIcon size={15} aria-hidden="true" data-id="element-2943" data-mp="9hf9czzjgkk4" />
              {t('eng.photos.add')}
            </Button>} data-id="element-2941" data-mp="20idn7prca2b">
          {t('eng.photos.timeline')}
        </SectionTitle>
        {addNote && <p className="mb-2 rounded-card border border-sand-line bg-sand-deep/60 p-2 text-[11.5px] text-deep" data-id="element-2944" data-mp="o85z82hkl9kl">
            {t('eng.photos.addedNote')}
          </p>}
        <ol className="rounded-card border border-sand-line bg-white/70 px-2" data-id="element-2945" data-mp="3mrw7quudbs6s">
          {progressPhotos.map(photo => <li key={photo.id} className="flex items-center gap-2 border-b border-sand-line py-2 last:border-b-0" data-id="element-2946" data-mp="itjejrqm39b">
              <img src={photo.url} alt={t('eng.photos.alt', {
            date: fmt(photo.date)
          })} className="h-16 w-12 shrink-0 rounded-[10px] object-cover" data-id="element-2947" data-mp="4lq4ch4r2bgf" />
              <span className="min-w-0 flex-1" data-id="element-2948" data-mp="1g1eutih7pslz">
                <span className="bee-num block text-[13px] font-medium text-deep" data-id="element-2949" data-mp="1ehd6jj05tkzk">{fmt(photo.date)}</span>
                <span className="block text-[11.5px] text-muted" data-id="element-2950" data-mp="19jv9apvi8u7e">{tl(photo.note)}</span>
              </span>
              <button type="button" onClick={() => setConfirmId(photo.id)} aria-label={`${t('eng.photos.share')} · ${fmt(photo.date)}`} className="grid h-11 w-11 shrink-0 place-items-center rounded-pill text-muted transition-colors duration-150 ease-brand hover:bg-sand-deep hover:text-deep" data-id="element-2951" data-mp="1ohdrf9dog95v">
                <Share2Icon size={16} data-id="element-2952" data-mp="277dejqpyje6n" />
              </button>
            </li>)}
        </ol>
      </section>

      <Modal open={confirmId !== null} onClose={() => setConfirmId(null)} labelledBy="photo-share-title" data-id="element-2953" data-mp="227hchx0tlwtq">
        <h2 id="photo-share-title" className="pr-5 text-section text-deep" data-id="element-2954" data-mp="2g8jxe6g2x132">
          {t('eng.photos.shareTitle')}
        </h2>
        <p className="mt-1 text-[13px] leading-relaxed text-muted" data-id="element-2955" data-mp="2c7rnrc3aou69">{t('eng.photos.shareBody')}</p>
        <div className="mt-3 space-y-2" data-id="element-2956" data-mp="2uv0gi6f01qti">
          <Button variant="primary" size="lg" fullWidth onClick={() => {
          setConfirmId(null);
          setSharing(true);
        }} data-id="element-2957" data-mp="2zt23v3jgkcas">
            {t('eng.photos.shareConfirm')}
          </Button>
          <Button variant="outline" size="lg" fullWidth onClick={() => setConfirmId(null)} data-id="element-2958" data-mp="2quglp9hwezaj">
            {t('eng.photos.shareCancel')}
          </Button>
        </div>
      </Modal>

      <ShareSheet open={sharing} onClose={() => setSharing(false)} data-id="element-2959" data-mp="2gvr3tgq05eua" />
    </div>;
}